import React from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, TrendingUp, Users, Award, RefreshCw } from 'lucide-react';

const CompensationModelPage = () => {
  const compensationTypes = [
    {
      icon: DollarSign,
      title: "Initial Commissions",
      description: "Earn commissions when policies are issued",
      details: "Commissions are paid by insurance carriers, not by clients. Rates vary by product type and carrier."
    },
    {
      icon: RefreshCw,
      title: "Renewal Commissions",
      description: "Ongoing income from active policies",
      details: "As long as your clients maintain their policies, you continue to earn renewal commissions year after year."
    },
    {
      icon: Users,
      title: "Leadership Income",
      description: "Override commissions from mentoring agents",
      details: "As you build and train your team, you earn override commissions on the production of agents you've helped develop."
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="compensation-model-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Link to="/for-advisors" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
              ← Back to For Advisors
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Compensation Model
            </h1>
            <p className="text-slate-300 text-xl">
              ShopCompareInsure advisors earn commissions paid by insurance carriers. Your income potential is directly tied to your effort and results.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl text-white mb-12 text-center">How Advisors Earn Income</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {compensationTypes.map((type, idx) => {
              const Icon = type.icon;
              return (
                <div key={idx} className="bg-slate-900/50 border border-slate-800 p-8 hover:border-[#C5A059]/30 transition-colors">
                  <div className="w-14 h-14 rounded-lg bg-[#C5A059]/10 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="text-white font-semibold text-xl mb-3">{type.title}</h3>
                  <p className="text-[#C5A059] text-sm mb-4">{type.description}</p>
                  <p className="text-slate-400 text-sm">{type.details}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">Income Potential</h2>
              <p className="text-slate-400 mb-6">
                Your income grows with the size of your client base and advisory network. There is no cap on what you can earn.
              </p>
              <div className="space-y-6">
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <p className="text-white font-semibold">Part-Time Advisors</p>
                  <p className="text-slate-400 text-sm">Many advisors start part-time while maintaining other income, earning supplemental income as they build their practice.</p>
                </div>
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <p className="text-white font-semibold">Full-Time Advisors</p>
                  <p className="text-slate-400 text-sm">Dedicated full-time advisors can build substantial income, with top performers earning six figures and beyond.</p>
                </div>
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <p className="text-white font-semibold">Agency Builders</p>
                  <p className="text-slate-400 text-sm">Those who build teams of advisors can create significant passive income streams through leadership overrides.</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 p-8">
              <Award className="w-12 h-12 text-[#C5A059] mb-6" />
              <h3 className="text-white font-semibold text-xl mb-4">Performance Recognition</h3>
              <p className="text-slate-400 text-sm mb-6">
                Top-performing advisors are recognized through awards programs, incentive trips, and advancement opportunities. Your success is celebrated and rewarded.
              </p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>• Monthly and annual production awards</li>
                <li>• Recognition at national conferences</li>
                <li>• Incentive trips and bonuses</li>
                <li>• Leadership title advancement</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Start Building Your Income</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Join ShopCompareInsure and begin your journey to financial freedom.
          </p>
          <Link
            to="/become-an-agent"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Apply Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CompensationModelPage;
